import type { HomeDashboard, ResumeTarget, TodayStudyTask } from '@/domain/Models';
import type { StudyTaskRepository } from '@/domain/Repositories';
import { db, getMeta } from './Database';
import type { SettingsRepositoryImpl } from './SettingsRepositoryImpl';

function epochDay(ms = Date.now()): number {
  const d = new Date(ms);
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
}

export class StudyTaskRepositoryImpl implements StudyTaskRepository {
  constructor(private readonly settings: SettingsRepositoryImpl) {}

  async dashboard(): Promise<HomeDashboard> {
    const today = epochDay();
    const settings = await this.settings.get();
    const existing = await db.studyTasks.get(today);
    const now = Date.now();
    const words = await db.vocabulary.where('active').equals(1).toArray();
    const memories = await db.wordMemory.toArray();
    const mem = new Map(memories.map((m) => [m.wordId, m]));
    let due = 0;
    let fresh = 0;
    for (const w of words) {
      const m = mem.get(w.id);
      if (!m || (m.reps === 0 && m.masteredUi === 0)) fresh += 1;
      else if (m.masteredUi === 0 && m.reps > 0 && m.dueAtEpochMs <= now) due += 1;
    }
    const newWordTarget = settings.dailyNewWordTarget;
    const task: TodayStudyTask = {
      dateEpochDay: today,
      newWordTarget,
      newWordDone: existing?.newWordDone ?? 0,
      reviewTarget: due,
      reviewDone: existing?.reviewDone ?? 0,
      recommendedScenarioId: existing?.recommendedScenarioId ?? null,
      studySeconds: existing?.studySeconds ?? 0,
      completed: existing?.completed === 1,
    };

    const last7: boolean[] = [];
    for (let i = 6; i >= 0; i -= 1) {
      const row = await db.studyTasks.get(today - i);
      last7.push((row?.studySeconds ?? 0) > 0 || (row?.newWordDone ?? 0) > 0);
    }
    let streak = 0;
    for (let i = 0; i <= 400; i += 1) {
      const row = await db.studyTasks.get(today - i);
      const active = (row?.studySeconds ?? 0) > 0 || (row?.newWordDone ?? 0) > 0;
      if (!active) {
        if (i === 0) continue;
        break;
      }
      streak += 1;
    }

    const resume: ResumeTarget[] = [];
    const vocabCp = await db.vocabCheckpoints
      .where('status')
      .equals('IN_PROGRESS')
      .reverse()
      .sortBy('updatedAtEpochMs');
    const latestVocab = vocabCp.at(-1);
    if (latestVocab) {
      const remaining = Math.max(0, JSON.parse(latestVocab.queueWordIdsJson).length - latestVocab.currentIndex);
      resume.push({
        kind: 'vocabulary',
        id: latestVocab.sessionId,
        title: '词汇复习',
        subtitle: `还剩 ${remaining} 个`,
        updatedAtEpochMs: latestVocab.updatedAtEpochMs,
      });
    }
    const scenarioCp = await db.scenarioSessions
      .where('status')
      .equals('IN_PROGRESS')
      .reverse()
      .sortBy('updatedAtEpochMs');
    const latestSc = scenarioCp.at(-1);
    if (latestSc) {
      const sc = await db.scenarios.get(latestSc.scenarioId);
      resume.push({
        kind: 'scenario',
        id: latestSc.scenarioId,
        title: sc?.title ?? '情景练习',
        subtitle: `第 ${latestSc.currentPairIndex + 1}/${latestSc.pairCount} 轮`,
        updatedAtEpochMs: latestSc.updatedAtEpochMs,
      });
    }
    resume.sort((a, b) => b.updatedAtEpochMs - a.updatedAtEpochMs);

    const recommended = await db.scenarios.where('active').equals(1).first();
    return {
      today: task,
      streakDays: streak,
      weekActivity: last7,
      totalStudySeconds: existing?.studySeconds ?? 0,
      dueCount: due,
      resume,
      recommendedScenarioId: recommended?.id ?? null,
      recommendedScenario: recommended
        ? {
            id: recommended.id,
            title: recommended.title,
            topic: recommended.topic,
            difficulty: recommended.difficulty,
            estimatedMinutes: recommended.estimatedMinutes,
          }
        : null,
      corpusContentVersion: await getMeta('active_corpus_content_version'),
    };
  }

  async addStudySeconds(seconds: number): Promise<void> {
    const today = epochDay();
    const existing = await db.studyTasks.get(today);
    const settings = await this.settings.get();
    await db.studyTasks.put({
      dateEpochDay: today,
      newWordTarget: existing?.newWordTarget ?? settings.dailyNewWordTarget,
      newWordDone: existing?.newWordDone ?? 0,
      reviewTarget: existing?.reviewTarget ?? 0,
      reviewDone: existing?.reviewDone ?? 0,
      recommendedScenarioId: existing?.recommendedScenarioId ?? null,
      studySeconds: (existing?.studySeconds ?? 0) + Math.max(0, seconds),
      completed: existing?.completed ?? 0,
      updatedAtEpochMs: Date.now(),
    });
  }
}
